import type { FieldDefinition } from "@/services/form-definition";

/**
 * Builds a lookup map from field name to {@link FieldDefinition}.
 * Later entries win if duplicate names appear (API should enforce uniqueness).
 *
 * @param definitions - OpenAPI field definitions from the form definition payload.
 */
export function buildFieldDefinitionsByName(
	definitions: FieldDefinition[] | null | undefined,
): Map<string, FieldDefinition> {
	const map = new Map<string, FieldDefinition>();
	if (!definitions) return map;

	for (const def of definitions) {
		const key = def.name?.trim();
		if (!key) continue;
		map.set(key, def);
	}
	return map;
}

/**
 * Builds a lookup map from OpenAPI `FieldDefinition.id` (UUID) to definition.
 * Entries without an id are skipped.
 */
export function buildFieldDefinitionsById(
	definitions: FieldDefinition[] | null | undefined,
): Map<string, FieldDefinition> {
	const map = new Map<string, FieldDefinition>();
	if (!definitions) return map;

	for (const def of definitions) {
		const key = def.id?.trim();
		if (!key) continue;
		map.set(key, def);
	}
	return map;
}

/**
 * Resolves a layout `fieldDefinitionId` to a definition (tries id, then name).
 */
export function resolveFieldDefinitionByLayoutId(
	fieldDefinitionId: string,
	byId: ReadonlyMap<string, FieldDefinition>,
	byName: ReadonlyMap<string, FieldDefinition>,
): FieldDefinition | undefined {
	const key = fieldDefinitionId?.trim() ?? "";
	if (!key) return undefined;
	return byId.get(key) ?? byName.get(key);
}
